// Núcleo PURO do parse do snapshot do catálogo: o MESMO código roda no worker
// (snapshot-parser.worker.ts), no fallback da thread principal
// (snapshot-parser-client.ts) e nos testes. Uma fonte só, para que o hash
// calculado no worker e o calculado na thread principal não possam divergir.
//
// Sem nada de DOM nem de `self`: importável dos dois lados. O worker só faz o
// transporte (`postMessage(responderParse(e.data))`); tudo o que ele decide
// está aqui, e os testes do cliente rodam ESTA função atrás de um Worker falso.
//
// ⚠️ SINTAXE: o chunk do worker NÃO passa pelo plugin-legacy (que só transpila
// os chunks de entrada do app). Ele sai com o `build.target` (es2019) e roda
// assim no Chromium da TV. Então nada aqui (nem no que for importado) pode usar
// sintaxe que o Chr53 (webOS 4.x) não parseia — ex.: `catch {` sem a variável é
// ES2019/Chr66, e derruba o worker INTEIRO no Chr53 com SyntaxError (medido no
// Chromium 53 genuíno). O worker quebrado não aparece: o cliente degrada pro
// parse na thread principal, calado. `workers-chunk.compat.test.ts` trava isso
// (sintaxe e APIs de runtime do Chr53, pra todo worker que a varredura achar).

import { hashConteudo } from '../lib/hash-conteudo'

export interface ParseRequest {
  id: number
  buffer: ArrayBuffer
  /** Surfaces cujo hash de conteúdo deve vir junto do parse. Ausente = nenhum. */
  hashSurfaces?: string[]
}

export interface ParseSuccess {
  id: number
  ok: true
  snapshot: unknown
  /** `null` quando não foi pedido ou não deu pra calcular: quem chamou calcula
   *  na thread principal se precisar. Hash nunca derruba o parse. */
  hashes: Record<string, string> | null
}

export interface ParseFailure {
  id: number
  ok: false
  error: string
}

export type ParseResponse = ParseSuccess | ParseFailure

/** Bytes UTF-8 → valor do JSON. Lança se o JSON for inválido (snapshot
 *  corrompido é erro real e deve propagar). */
export function decodificarJson(buffer: ArrayBuffer): unknown {
  const text = new TextDecoder('utf-8').decode(new Uint8Array(buffer))
  return JSON.parse(text) as unknown
}

/**
 * Hash de conteúdo (lib/hash-conteudo.ts) de cada surface pedida do snapshot.
 *
 * É exatamente o que a thread principal calcularia sobre a cópia que recebe do
 * worker: o `postMessage` faz structured clone, que preserva a ordem das chaves
 * e os valores, então `JSON.stringify` dá a mesma string dos dois lados. O
 * teste `snapshot-parse-core.test.ts` prova isso com um structured clone real.
 */
export function hashesDasSurfaces(
  snapshot: Readonly<Record<string, unknown>>,
  surfaces: readonly string[],
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const s of surfaces) out[s] = hashConteudo(snapshot[s])
  return out
}

/** O que o worker responde a um pedido. Hash é otimização: qualquer problema
 *  nele vira `hashes: null`, nunca falha de parse. */
export function responderParse(req: ParseRequest): ParseResponse {
  let snapshot: unknown
  try {
    snapshot = decodificarJson(req.buffer)
  } catch (err) {
    return { id: req.id, ok: false, error: err instanceof Error ? err.message : String(err) }
  }
  let hashes: Record<string, string> | null = null
  const pedidas = req.hashSurfaces
  if (pedidas && pedidas.length > 0 && snapshot !== null && typeof snapshot === 'object') {
    try {
      hashes = hashesDasSurfaces(snapshot as Record<string, unknown>, pedidas)
    } catch (erro) {
      // Com a variável de propósito: `catch {` não parseia no Chr53 (ver topo).
      // Hash é otimização: sem ele, a thread principal recalcula se precisar.
      void erro
      hashes = null
    }
  }
  return { id: req.id, ok: true, snapshot, hashes }
}
